import CryptoJS from 'crypto-js'

/**
 * 签名工具类
 * 对应 Java 版本：com.moji.module.cms.utils.SignatureUtil
 */
export class SignatureUtil {
  /**
   * 生成签名
   * @param params Query 参数 (Map<String, String>)
   * @param body Request Body 字符串
   * @param headers Header 参数 (Map<String, String>)，通常包含 appId, timestamp, nonce
   * @param appSecret 应用密钥
   * @returns 签名字符串 (Hex)
   */
  static generateSignature(
    params: Record<string, any>,
    body: string,
    headers: Record<string, any>,
    appSecret: string
  ): string {
    const signString = this.buildSignString(params, body, headers, appSecret)
    return CryptoJS.SHA256(signString).toString(CryptoJS.enc.Hex)
  }

  /**
   * 构建待签名字符串
   * 逻辑：MapUtil.join(parameterMap, "&", "=") + requestBody + MapUtil.join(headerMap, "&", "=") + appSecret
   */
  static buildSignString(
    params: Record<string, any>,
    body: string,
    headers: Record<string, any>,
    appSecret: string
  ): string {
    // 1. 处理 parameterMap (对应 Java 的 HashMap，但在签名中通常需要排序以保证一致性)
    // Java 代码中 parameterMap 为空，但如果有值，MapUtil.join 会将其连接
    const paramString = this.sortAndJoin(params)

    // 2. requestBody 直接拼接
    // 注意：Java 代码中是直接拼接字符串
    const bodyString = body || ''

    // 3. 处理 headerMap (对应 Java 的 TreeMap，自动按 Key 排序)
    const headerString = this.sortAndJoin(headers)

    // 4. 拼接 appSecret
    // 最终格式：paramString + bodyString + headerString + appSecret
    return paramString + bodyString + headerString + appSecret
  }

  /**
   * 将 Map 转换为 key=value&key=value 形式，并按 key 排序
   */
  private static sortAndJoin(data: Record<string, any>): string {
    if (!data) return ''
    const keys = Object.keys(data).sort() // TreeMap 效果，按 key 排序
    const list: string[] = []
    
    keys.forEach((key) => {
      const val = data[key]
      // 过滤掉 undefined 和 null，保留空字符串 (视具体需求而定，Java MapUtil 通常包含空值如果是空串)
      // Java 示例中都是 String 类型
      if (val !== undefined && val !== null) {
        list.push(`${key}=${String(val)}`)
      }
    })
    
    return list.join('&')
  }

  /**
   * 获取当前时间戳 (毫秒)
   */
  static getTimestamp(): number {
    return new Date().getTime()
  }

  /**
   * 生成随机 Nonce (UUID)
   */
  static generateNonce(): string {
    return this.uuid()
  }

  /**
   * 简单的 UUID 生成实现
   */
  private static uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
}
